import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import {
  Box,
  Container,
  Stepper,
  Step,
  StepLabel,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Grid,
  Typography,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { fetchTemplateById } from "../redux/slices/templateSlice";
import {
  createDocument,
  updateDocument,
  getDocument,
  setAnswers,
  completeDocument,
  clearCurrentDocument,
} from "../redux/slices/documentSlice";
import AnswerForm from "../components/templates/AnswerForm";
import DocumentPreview from "../components/templates/DocumentPreview";
import DocumentDownloadButtons from "../components/documents/DocumentDownloadButtons";
import CreditPurchaseModal from "../components/credits/CreditPurchaseModal";
import { checkBalance } from "../redux/slices/creditSlice";
import { getSettingValue } from "../redux/slices/appSettingSlice";
import { useHasPermission } from "../hooks/usePermission";
import alertify from 'alertifyjs';

const DocumentWizardPage = () => {
  const { templateId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const [currentStep, setCurrentStep] = useState(0);
  const [mobileTab, setMobileTab] = useState(0);
  const [questionsCompleted, setQuestionsCompleted] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [renderedContentHtml, setRenderedContentHtml] = useState(null);
  const [titleDialogOpen, setTitleDialogOpen] = useState(false);
  const [documentTitle, setDocumentTitle] = useState('');
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const [balanceCheck, setBalanceCheck] = useState(null);
  const [requiredCredits, setRequiredCredits] = useState(null);
  const updateTimeoutRef = useRef(null);
  const documentCreatedRef = useRef(false);
  const documentCreationPromiseRef = useRef(null);
  const requestIdRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const {
    currentTemplate: template,
    loading,
    error,
  } = useSelector((state) => state.templates);
  const { currentDocument } = useSelector((state) => state.documents);
  const { balance } = useSelector((state) => state.credits);
  const { settingValues } = useSelector((state) => state.appSettings);
  const { user } = useSelector((state) => state.auth);
  const hasCompanyAdmin = useHasPermission('companyadminpermission');

  useEffect(() => {
    if (templateId) {
      documentCreatedRef.current = false;
      documentCreationPromiseRef.current = null;
      requestIdRef.current = null;
      dispatch(clearCurrentDocument());
      dispatch(fetchTemplateById(templateId));
    }
  }, [templateId, dispatch]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const draftDocumentId = urlParams.get("documentId");
    if (draftDocumentId && !currentDocument) {
      dispatch(getDocument(draftDocumentId));
    }
  }, [dispatch, currentDocument]);


  const ensureDocument = useCallback(async (initialAnswers = {}) => {
    const doc = currentDocument?.document || currentDocument;
    const existingDocumentId = doc?._id || doc?.id;
    
    if (existingDocumentId) {
      return existingDocumentId;
    }

    if (documentCreationPromiseRef.current) {
      try {
        return await documentCreationPromiseRef.current;
      } catch (error) {
        documentCreationPromiseRef.current = null;
      }
    }

    if (!template || !templateId) {
      throw new Error('Template bulunamadı');
    }

    if (!requestIdRef.current) {
      requestIdRef.current = uuidv4();
    }

    const creationPromise = (async () => {
      documentCreatedRef.current = true;

      try {
        const result = await dispatch(
          createDocument({ 
            templateId, 
            initialAnswers,
            requestId: requestIdRef.current
          })
      ).unwrap();
        
        const newDocument = result.document || result;
        const newDocumentId = newDocument._id || newDocument.id;
        
        return newDocumentId;
      } catch (error) {
        documentCreatedRef.current = false;
        documentCreationPromiseRef.current = null;
        throw error;
      }
    })();

    documentCreationPromiseRef.current = creationPromise;

    try {
      const documentId = await creationPromise;
      documentCreationPromiseRef.current = null;
      return documentId;
    } catch (error) {
      documentCreationPromiseRef.current = null;
      throw error;
    }
  }, [dispatch, template, templateId, currentDocument]);

  const steps = ["Soruları Cevapla", "Önizleme", "Tamamlandı"];

  const handleNext = useCallback(async () => {
    // Soru kontrolü
    const currentDoc = currentDocument?.document || currentDocument;
    const questionTree = currentDoc?.templateSnapshot?.questionTree || template?.questionTree || [];
    const questions = template?.questions || [];
    const hasQuestions = (questionTree && Array.isArray(questionTree) && questionTree.length > 0) ||
                        (questions && Array.isArray(questions) && questions.length > 0);
    
    // Soru yoksa questionsCompleted kontrolünü atla, soru varsa kontrol et
    if (currentStep === 0 && !questionsCompleted && hasQuestions) {
      return;
    }
    
    if (currentStep === 0 && currentStep < steps.length - 1) {
      try {
        await ensureDocument({});
        setCurrentStep(1);
      } catch (error) {
        alert(error.message || 'Document oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.');
      }
    } else if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  }, [currentStep, questionsCompleted, steps.length, ensureDocument, template, currentDocument]);

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      history.push(`/documents/new/${templateId}`);
    }
  };

  const handleAnswerChange = useCallback(
    async (newAnswers) => {
      dispatch(setAnswers(newAnswers));

      try {
        const documentId = await ensureDocument(newAnswers);
        
        if (!documentId) {
        return;
      }

        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }

        updateTimeoutRef.current = setTimeout(() => {
      dispatch(
        updateDocument({
              documentId: documentId,
          answers: newAnswers,
        })
      );
        }, 3000);
      } catch (error) {
        // Silent error handling
      }
    },
    [dispatch, ensureDocument]
  );

  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  const handleQuestionsComplete = useCallback(() => {
    setQuestionsCompleted(true);
    setCurrentStep(1);
  }, []);

  useEffect(() => {
    if (currentStep === 0) {
      setQuestionsCompleted(false);
    }
  }, [currentStep]);

  const handleMobileTabChange = (event, newValue) => {
    setMobileTab(newValue);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || "Template yüklenirken bir hata oluştu"}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.goBack()}>
          Geri Dön
        </Button>
      </Container>
    );
  }

  if (!template) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Template bulunamadı
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.goBack()}>
          Geri Dön
        </Button>
      </Container>
    );
  }

  const renderStep0 = () => {
    if (!template) {
      return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "60vh",
            }}>
            <CircularProgress />
          </Box>
        </Container>
      );
    }

    // Use snapshot if available, otherwise fallback to template
    const currentDoc = currentDocument?.document || currentDocument;
    const questionTree = currentDoc?.templateSnapshot?.questionTree || template?.questionTree || [];
    const templateContentHtml = currentDoc?.templateSnapshot?.contentHtml || template?.contentHtml || '';
    const answers = currentDoc?.answers || {};

    if (isMobile) {
      return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Box sx={{ flexShrink: 0 }}>
          <Tabs
            value={mobileTab}
            onChange={handleMobileTabChange}
            variant="fullWidth"
              sx={{ mb: 2 }}>
            <Tab label="Cevaplar" />
            <Tab label="Önizleme" />
          </Tabs>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {mobileTab === 0 ? (
            <Paper
              sx={{
                  p: 2,
                height: "100%",
                  width: "100%",
                display: "flex",
                flexDirection: "column",
                  minHeight: 0,
                  overflow: "hidden",
                  boxSizing: "border-box",
              }}>
              <AnswerForm
                questions={[]}
                questionTree={questionTree}
                answers={answers}
                onChange={handleAnswerChange}
                onClear={() => handleAnswerChange({})}
                onComplete={handleQuestionsComplete}
              />
            </Paper>
          ) : (
              <Box 
                sx={{ 
                  height: '100%', 
                  overflow: 'auto',
                  "&::-webkit-scrollbar": {
                    width: "6px",
                    height: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "rgba(0, 0, 0, 0.2)",
                    borderRadius: "3px",
                    transition: "background 0.2s ease",
                    "&:hover": {
                      background: "rgba(0, 0, 0, 0.35)",
                    },
                  },
                  scrollbarWidth: "thin",
                  scrollbarColor: "rgba(0, 0, 0, 0.2) transparent",
                }}>
            <DocumentPreview
              content={templateContentHtml}
              answers={answers}
              questionTree={questionTree}
              onRender={setRenderedContentHtml}
              status={currentDoc?.status || 'draft'}
              creditsUsed={currentDoc?.creditsUsed || 0}
              showOptionalPlaceholders={currentStep === 0 ? true : false}
            />
              </Box>
          )}
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Grid container spacing={2} sx={{ flex: 1, minHeight: 0, height: '100%' }}>
          <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
            <Paper
              sx={{
                p: 2,
                height: "100%",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                overflow: "hidden",
                boxSizing: "border-box",
              }}>
              <AnswerForm
                questions={[]}
                questionTree={questionTree}
                answers={answers}
                onChange={handleAnswerChange}
                onClear={() => handleAnswerChange({})}
                onComplete={handleQuestionsComplete}
              />
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
            <Box 
              sx={{ 
                height: "100%", 
                overflow: "auto", 
                minHeight: 0,
                "&::-webkit-scrollbar": {
                  width: "6px",
                  height: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "rgba(0, 0, 0, 0.2)",
                  borderRadius: "3px",
                  transition: "background 0.2s ease",
                  "&:hover": {
                    background: "rgba(0, 0, 0, 0.35)",
                  },
                },
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(0, 0, 0, 0.2) transparent",
              }}>
              <DocumentPreview
                content={templateContentHtml}
                answers={answers}
                questionTree={questionTree}
                onRender={setRenderedContentHtml}
                status={currentDoc?.status || 'draft'}
                creditsUsed={currentDoc?.creditsUsed || 0}
                showOptionalPlaceholders={currentStep === 0 ? true : false}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderStep1 = () => {
    if (!template) {
      return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "60vh",
            }}>
            <CircularProgress />
          </Box>
        </Container>
      );
    }

    const doc = currentDocument?.document || currentDocument;
    const currentDoc = doc;
    const documentId = doc?._id || doc?.id;
    
    // Soru kontrolü
    const checkQuestionTree = currentDoc?.templateSnapshot?.questionTree || template?.questionTree || [];
    const checkQuestions = template?.questions || [];
    const hasQuestions = (checkQuestionTree && Array.isArray(checkQuestionTree) && checkQuestionTree.length > 0) ||
                        (checkQuestions && Array.isArray(checkQuestions) && checkQuestions.length > 0);
    
    if (!currentDocument || !documentId) {
      // Soru yoksa direkt devam et, soru varsa uyarı göster
      if (hasQuestions) {
        return (
          <Container maxWidth="xl" sx={{ py: 4 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Lütfen önce soruları cevaplayın.
            </Alert>
            <Button
              variant="outlined"
              onClick={() => setCurrentStep(0)}
            >
              Sorulara Dön
            </Button>
          </Container>
        );
      }
      // Soru yoksa document oluştur ve devam et
      ensureDocument({}).then(() => {
        // Document oluşturuldu, sayfa yenilenecek
      }).catch(() => {
        // Silent error
      });
      return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "60vh",
            }}>
            <CircularProgress />
          </Box>
        </Container>
      );
    }

    // Use snapshot if available, otherwise fallback to template
    const previewDoc = currentDoc;
    const questionTree = previewDoc?.templateSnapshot?.questionTree || template?.questionTree || [];
    const templateContentHtml = previewDoc?.templateSnapshot?.contentHtml || template?.contentHtml || '';
    const answers = previewDoc?.answers || {};

    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* Balance Warning (Optional - UX improvement) */}
        {balanceCheck && !balanceCheck.hasEnough && (
          <Alert 
            severity="warning" 
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setRequiredCredits(balanceCheck.required);
                  setPurchaseModalOpen(true);
                }}
              >
                Hemen Kredi Yükle
              </Button>
            }
          >
            <Typography variant="body2">
              <strong>Yetersiz Bakiye:</strong> Gerekli: {balanceCheck.required} Kredi, Mevcut: {balanceCheck.currentBalance} Kredi
              <Box component="br" />
              {(() => {
                // T7.6: User type-based warning messages
                if (user?.companyId) {
                  // Company user
                  if (hasCompanyAdmin) {
                    // Company owner/admin
                    return 'Şirket bakiyeniz yetersiz. Lütfen kredi yükleyin.';
                  } else {
                    // Company member
                    return 'Şirket bakiyeniz yetersiz. Lütfen şirket yöneticinizle iletişime geçin.';
                  }
                } else {
                  // Individual user
                  return 'Yetersiz bakiye. Lütfen kredi yükleyin.';
                }
              })()}
            </Typography>
          </Alert>
        )}

        <Box 
          sx={{ 
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            "&::-webkit-scrollbar": {
              width: "6px",
              height: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(0, 0, 0, 0.2)",
              borderRadius: "3px",
              transition: "background 0.2s ease",
              "&:hover": {
                background: "rgba(0, 0, 0, 0.35)",
              },
            },
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(0, 0, 0, 0.2) transparent",
          }}>
          <DocumentPreview
            content={templateContentHtml}
            answers={answers}
            questionTree={questionTree}
            onRender={setRenderedContentHtml}
            status={currentDoc?.status || 'draft'}
            creditsUsed={currentDoc?.creditsUsed || 0}
            showOptionalPlaceholders={currentStep === 0 ? true : false}
          />
        </Box>
      </Box>
    );
  };

  const renderStep2 = () => {
    if (!currentDocument) {
      return (
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "60vh",
            }}>
            <CircularProgress />
          </Box>
        </Container>
      );
    }

    const doc = currentDocument?.document || currentDocument;
    const documentId = doc?._id || doc?.id;
    const documentTitle = doc?.title || '';
    const documentStatus = doc?.status || 'completed';
    const creditsUsed = doc?.creditsUsed || 0;

    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 3,
          }}>
          <Typography variant="h4" fontWeight="bold" color="success.main">
            Belge Başarıyla Oluşturuldu!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Belgeniz hazır. Aşağıdaki seçeneklerden birini seçebilirsiniz.
          </Typography>
          {creditsUsed > 0 && (
            <Alert severity="info" sx={{ maxWidth: 400 }}>
              <Typography variant="body2">
                Bu belge için <strong>{creditsUsed} kredi</strong> kullanıldı.
              </Typography>
            </Alert>
          )}
          
          {/* Download Buttons */}
          {documentId && documentStatus === 'completed' && (
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 1 }}>
              <DocumentDownloadButtons
                documentId={documentId}
                documentTitle={documentTitle}
                status={documentStatus}
              />
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "center", mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => history.push('/documents')}
            >
              Belgelerime Git
            </Button>
            <Button
              variant="contained"
              onClick={() => history.push('/documents/new')}
            >
              Yeni Belge Oluştur
            </Button>
          </Box>
        </Box>
      </Container>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return renderStep0();
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      default:
        return renderStep0();
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", display: "flex", flexDirection: "column" }}>
      <Container maxWidth="xl" sx={{ py: 2, flexShrink: 0 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 0 }}>
          Geri Dön
        </Button>

        <Paper sx={{ pl: 2, pr: 2, py: 1, mb: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {currentStep !== 2 && (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
                onClick={currentStep === 1 ? () => setCurrentStep(0) : handleBack}
              disabled={currentStep === 0}
              sx={{ minWidth: "120px" }}>
                {currentStep === 1 ? "Düzenle" : "Geri"}
            </Button>
            )}

            <Box sx={{ flex: 1 }}>
              <Stepper
                activeStep={currentStep}
                alternativeLabel
                sx={{
                  "& .MuiStepLabel-root": {
                    paddingTop: 0,
                  },
                  "& .MuiStepLabel-label": {
                    fontSize: "0.875rem",
                    marginTop: "4px",
                  },
                  "& .MuiStepConnector-root": {
                    top: "12px",
                  },
                }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

            {currentStep === 1 ? (
              <Button
                variant="contained"
                onClick={() => {
                  const doc = currentDocument?.document || currentDocument;
                  const currentTitle = doc?.title || '';
                  setDocumentTitle(currentTitle);
                  setTitleDialogOpen(true);
                }}
                disabled={completing}
                sx={{ minWidth: "120px" }}>
                Tamamla
              </Button>
            ) : currentStep < steps.length - 1 &&
            (currentStep !== 0 || questionsCompleted) ? (
              <Button
                variant="contained"
                onClick={handleNext}
                sx={{ minWidth: "120px" }}>
                İleri
              </Button>
            ) : (
              <Box sx={{ minWidth: "120px" }} />
            )}
          </Box>
        </Paper>
      </Container>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", width: "100%", display: "flex", flexDirection: "column" }}>
        <Box sx={{ height: "calc(100vh - 250px)", minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", width: "100%", maxWidth: "1536px", mx: "auto", px: 2 }}>
          {renderCurrentStep()}
        </Box>
      </Box>

      {/* Title Edit Dialog */}
      <Dialog
        open={titleDialogOpen}
        onClose={() => !completing && setTitleDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Belge Başlığını Düzenle</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Belgeyi tamamlamadan önce başlığını düzenleyebilirsiniz.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Belge Başlığı"
            fullWidth
            variant="outlined"
            value={documentTitle}
            onChange={(e) => setDocumentTitle(e.target.value)}
            placeholder="Belge başlığını girin..."
            disabled={completing}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setTitleDialogOpen(false)}
            disabled={completing}
          >
            İptal
          </Button>
          <Button
            onClick={async () => {
              setCompleting(true);
              try {
                const documentId = await ensureDocument({});
                
                if (!documentId) {
                  alertify.error('Document bulunamadı. Lütfen sayfayı yenileyin.');
                  setCompleting(false);
                  setTitleDialogOpen(false);
                  return;
                }
                
                const doc = currentDocument?.document || currentDocument;
                const currentAnswers = doc?.answers || {};
                
                // Calculate required credits (from template or default)
                // Document tamamlarken: template.creditCost varsa onu kullan, yoksa default_template_credits kullan
                let calculatedRequiredCredits = null;
                if (template?.creditCost !== null && template?.creditCost !== undefined) {
                  calculatedRequiredCredits = parseFloat(template.creditCost);
                } else {
                  // Try to get default_template_credits from app settings (Şirket/user template'leri için default kredi miktarı)
                  try {
                    const defaultTemplateCredits = settingValues.default_template_credits || await dispatch(getSettingValue('default_template_credits')).unwrap()?.value || '1';
                    calculatedRequiredCredits = parseFloat(defaultTemplateCredits);
                  } catch (err) {
                    calculatedRequiredCredits = 1; // Fallback
                  }
                }
                setRequiredCredits(calculatedRequiredCredits);

                // Optional: Pre-check balance (UX improvement)
                // Not: Bu kontrol opsiyonel, hata alırsa backend'de de kontrol yapılacak
                try {
                  if (calculatedRequiredCredits > 0) {
                    const check = await dispatch(checkBalance(calculatedRequiredCredits)).unwrap();
                    if (!check.hasEnough) {
                      setBalanceCheck(check);
                      setCompleting(false);
                      setTitleDialogOpen(false);
                      setPurchaseModalOpen(true);
                      return;
                    }
                  }
                } catch (err) {
                  // Balance check failed, continue (backend will check)
                }
                
                await dispatch(completeDocument({ 
                  documentId, 
                  contentHtml: renderedContentHtml || null,
                  answers: currentAnswers,
                  title: documentTitle.trim() || null
                })).unwrap();
                setTitleDialogOpen(false);
                setCurrentStep(2);
                alertify.success('Belge başarıyla tamamlandı');
              } catch (error) {
                // Check for 402 Payment Required error
                if (error?.statusCode === 402 || error?.response?.status === 402) {
                  const errorMessage = error?.message || error?.data?.message || error?.payload?.message || 'Yetersiz bakiye';
                  setBalanceCheck({
                    hasEnough: false,
                    required: requiredCredits || 1,
                    currentBalance: balance || 0,
                    deficit: (requiredCredits || 1) - (balance || 0)
                  });
                  setCompleting(false);
                  setTitleDialogOpen(false);
                  setPurchaseModalOpen(true);
                  alertify.warning(errorMessage);
                } else {
                  const errorMessage = error?.message || error?.data?.message || error?.payload?.message || 'Belge tamamlanırken bir hata oluştu';
                  alertify.error(errorMessage);
                }
              } finally {
                setCompleting(false);
              }
            }}
            variant="contained"
            disabled={completing}
          >
            {completing ? 'Tamamlanıyor...' : 'Tamamla'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Credit Purchase Modal */}
      <CreditPurchaseModal
        open={purchaseModalOpen}
        onClose={() => setPurchaseModalOpen(false)}
        onSuccess={async () => {
          // Payment successful, try to complete document again
          setPurchaseModalOpen(false);
          setCompleting(true);
          try {
            const documentId = await ensureDocument({});
            if (!documentId) {
              alertify.error('Document bulunamadı. Lütfen sayfayı yenileyin.');
              setCompleting(false);
              return;
            }
            
            const doc = currentDocument?.document || currentDocument;
            const currentAnswers = doc?.answers || {};
            await dispatch(completeDocument({ 
              documentId, 
              contentHtml: renderedContentHtml || null,
              answers: currentAnswers,
              title: documentTitle.trim() || null
            })).unwrap();
            setTitleDialogOpen(false);
            setCurrentStep(2);
            alertify.success('Belge başarıyla tamamlandı');
          } catch (error) {
            if (error?.statusCode === 402 || error?.response?.status === 402) {
              alertify.error('Hala yetersiz bakiye. Lütfen daha fazla kredi yükleyin.');
              setPurchaseModalOpen(true);
            } else {
              const errorMessage = error?.message || error?.data?.message || error?.payload?.message || 'Belge tamamlanırken bir hata oluştu';
              alertify.error(errorMessage);
            }
          } finally {
            setCompleting(false);
          }
        }}
        requiredCredits={requiredCredits}
      />
    </Box>
  );
};

export default DocumentWizardPage;
